import React, {useState} from 'react';
import Input from "../../ui/Input/Input";
import Button from "../../ui/Button/Button";
import IconGoogle from "../../ui/Icons/IconGoogle";
import {NavLink, useNavigate} from "react-router-dom";
import './AuthForm.scss';
import {auth_get_profile, auth_session} from "../../../api/auth_api";
import {useProfileStore} from "../../../store/profile/pofile.store";
import {useLoaderStore} from "../../../store/loader/loader.store";
import {
    firebase_sign_in_with_email,
    firebase_sign_in_with_google,
    firebase_sign_up_with_email,
    sendPasswordReset
} from "../../../services/firebase_services";
import {toast} from "react-toastify";
import AgreementCheckbox from "../../ui/AgreementCheckbox/AgreementCheckbox";
import {AlertCircle, MailCheck} from "lucide-react";

interface AuthFormType {
    type: 'signup' | 'login' | 'reset';
}

// Firebase auth error codes → messages people can act on
const AUTH_ERRORS: Record<string, string> = {
    'auth/invalid-credential': 'Wrong email or password.',
    'auth/wrong-password': 'Wrong email or password.',
    'auth/user-not-found': 'Wrong email or password.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/email-already-in-use': 'An account with this email already exists. Try signing in.',
    'auth/weak-password': 'Password should be at least 6 characters.',
    'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
    'auth/missing-password': 'Please enter your password.',
    'auth/network-request-failed': 'No connection. Check your internet and try again.',
    'auth/missing-email': 'Please enter your email address.',
};

const authErrorMessage = (err: any) => {
    return AUTH_ERRORS[err?.code] || 'Something went wrong. Please try again.';
};

const AuthForm = ({type}: AuthFormType) => {

    const {updateUser} = useProfileStore();
    const {showLoader, hideLoader} = useLoaderStore();
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [resetSentTo, setResetSentTo] = useState('');

    // After Firebase sign-in: make sure the Soulify profile exists, then load it
    const finishSignIn = async (name?: string) => {
        await auth_session(name ? {name} : {});
        const profile = await auth_get_profile();
        updateUser(profile.data);
        navigate('/');
    };

    const signIn = async (e: any) => {
        e.preventDefault();
        setErrorMessage('');
        showLoader('Signing in...');
        try {
            await firebase_sign_in_with_email(e.target[0].value, e.target[1].value);
            await finishSignIn();
        } catch (err) {
            setErrorMessage(authErrorMessage(err));
        } finally {
            hideLoader();
        }
    }

    const signUp = async (e: any) => {
        e.preventDefault();
        if (!termsAccepted) return;
        if (e.target[2].value !== e.target[3].value) {
            setErrorMessage("Passwords don't match.");
            return;
        }
        setErrorMessage('');
        showLoader('Creating profile...');
        const name = e.target[0].value.trim();
        const email = e.target[1].value.trim();
        try {
            await firebase_sign_up_with_email(name, email, e.target[2].value);
            await finishSignIn(name);
            toast.info(`We sent a verification link to ${email}`);
        } catch (err) {
            setErrorMessage(authErrorMessage(err));
        } finally {
            hideLoader();
        }
    }

    const resetPassword = async (e: any) => {
        e.preventDefault();
        setErrorMessage('');
        const email = e.target[0].value.trim();
        showLoader('Sending link...');
        try {
            await sendPasswordReset(email);
            setResetSentTo(email);
        } catch (err: any) {
            // Don't reveal whether an account exists for this email
            if (err?.code === 'auth/user-not-found') {
                setResetSentTo(email);
            } else {
                setErrorMessage(authErrorMessage(err));
            }
        } finally {
            hideLoader();
        }
    }

    const signInWithGoogle = async () => {
        setErrorMessage('');
        try {
            await firebase_sign_in_with_google();
        } catch (err: any) {
            // Closing the Google popup isn't an error worth showing
            if (err?.code !== 'auth/popup-closed-by-user' && err?.code !== 'auth/cancelled-popup-request') {
                setErrorMessage('Google sign-in failed. Please try again.');
            }
            return;
        }
        try {
            showLoader('Signing in...');
            await finishSignIn();
        } catch (err) {
            setErrorMessage('Google sign-in failed. Please try again.');
        } finally {
            hideLoader();
        }
    }

    const errorBox = errorMessage ? <div className="form__error" role="alert">
        <AlertCircle size={16}/>
        <span>{errorMessage}</span>
    </div> : null;

    const signInForm = () => (
        <form onSubmit={signIn} className="form" action="">
            <div className="form__header">
                <h2>Welcome back</h2>
                <p>Sign in to continue your conversations.</p>
            </div>
            <Input name={'email'} autoComplete={'email'} title={'Email'} type={'email'} required/>
            <Input name={'password'} autoComplete={'current-password'} title={'Password'} type={'password'} required/>
            <NavLink to={'/auth/reset'} className="form__forgot">Forgot password?</NavLink>
            {errorBox}
            <Button variant={'primary'} title={'Sign in'}/>
            <div className="flex">
                <hr/>
                <span>or</span>
                <hr/>
            </div>
            <Button onClick={signInWithGoogle} type={'button'} icon={<IconGoogle/>} variant={'secondary'}
                    title={'Continue with Google'}/>
            <span className="form__legal">
                By continuing, you agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and
                {" "}<a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </span>
            <span className="form__switch">
                New to Soulify? <NavLink to={'signup'}>Create an account</NavLink>
            </span>
        </form>
    )

    const signUpForm = () => (
        <form onSubmit={signUp} className="form" action="">
            <div className="form__header">
                <h2>Create your account</h2>
                <p>Free to start. No credit card needed.</p>
            </div>
            <Input autoComplete={'name'} name={'name'} title={'Your name'} type={'text'} required/>
            <Input autoComplete={'email'} name={'email'} title={'Email'} type={'email'} required/>
            <Input autoComplete={'new-password'} name={'password'} title={'Password (6+ characters)'}
                   type={'password'} required minLength={6}/>
            <Input autoComplete={'new-password'} name={'password'} title={'Confirm password'} type={'password'}
                   required/>
            <AgreementCheckbox required checked={termsAccepted} onChange={setTermsAccepted}>
                I am at least 18 years old and agree to the <a href="/terms" target="_blank"
                                                               rel="noopener noreferrer">Terms of Service</a> and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                I understand that chats are AI simulations, not real people.
            </AgreementCheckbox>
            {errorBox}
            <Button disabled={!termsAccepted} variant={'primary'} title={'Create account'}/>
            <div className="flex">
                <hr/>
                <span>or</span>
                <hr/>
            </div>
            <Button disabled={!termsAccepted} onClick={signInWithGoogle} type={'button'} icon={<IconGoogle/>}
                    variant={'secondary'} title={'Sign up with Google'}/>
            <span className="form__switch">
                Already have an account? <NavLink to={'/auth'}>Sign in</NavLink>
            </span>
        </form>
    )

    const resetForm = () => (
        resetSentTo ? <div className="form">
                <div className="form__header">
                    <span className="icon-tile icon-tile--gradient form__icon"><MailCheck size={22}/></span>
                    <h2>Check your inbox</h2>
                    <p>If an account exists for <b>{resetSentTo}</b>, we've sent a link to set a new password. It
                        can take a minute, and may land in spam.</p>
                </div>
                <Button type={'button'} variant={'secondary'} title={'Use a different email'}
                        onClick={() => setResetSentTo('')}/>
                <span className="form__switch">
                    Remembered it? <NavLink to={'/auth'}>Back to sign in</NavLink>
                </span>
            </div>
            : <form onSubmit={resetPassword} className="form" action="">
                <div className="form__header">
                    <h2>Reset your password</h2>
                    <p>Enter the email you signed up with and we'll send you a link to set a new password.</p>
                </div>
                <Input name={'email'} autoComplete={'email'} title={'Email'} type={'email'} required/>
                {errorBox}
                <Button variant={'primary'} title={'Send reset link'}/>
                <span className="form__switch">
                    Remembered it? <NavLink to={'/auth'}>Back to sign in</NavLink>
                </span>
                <span className="form__legal">Signed up with Google? Just use "Continue with Google" on the sign-in
                    page.</span>
            </form>
    )

    switch (type) {
        case 'signup':
            return signUpForm()
        case 'reset':
            return resetForm()
        default:
            return signInForm()
    }
    ;
};

export default AuthForm;
